import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useForm } from '../../hooks/form-hook';
import { useAuth } from '../../hooks/useAuth';
import Card from '../../components/UI/Card/Card';
import Input from '../../components/UI/Input/Input';
import { useLoginMutation, isTwoFactorChallengeResponse } from '../../services/api';
import Button from '../../components/UI/Button/Button';
import RememberMe from '../../components/RememberMe/RememberMe';
import ErrorModal from '../../components/UI/ErrorModal/ErrorModal';
import Athenalogo from '../../assets/images/Athena_logo_final.png';
import { SupportInfo } from '../../components/SupportInfo/SupportInfo';
import { VALIDATOR_EMAIL, VALIDATOR_MINLENGTH } from '../../utils/validators';
import LoadingSpinner from '../../components/UI/LoadingSpinner/LoadingSpinner';
import { isFetchBaseQueryError } from '../../utils/isFetchBaseQueryError';
import './Login.css';

const Login = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const { state, pathname, search, hash } = useLocation();
  let urlBeforeRedirect = '';
  if (state && state.nextPath) {
    urlBeforeRedirect = state.nextPath;
  } else if (pathname !== '/login') {
    urlBeforeRedirect = `${pathname}${search}${hash}`;
  }

  const [count, setCount] = useState(0);
  const [rememberMe, setRememberMe] = useState(false);
  const [login, { isError, error: loginError, isLoading, reset }] = useLoginMutation();

  const onPasswordResetClick = () => {
    navigate('/forgotpassword');
  };
  const [formState, inputHandler] = useForm(
    {
      email: {
        value: '',
        isValid: false
      },
      password: {
        value: '',
        isValid: false
      }
    },
    false
  );

  const buttonClickHandler = () => {
    setCount(count + 1);
  };

  const authSubmitHandler = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    try {
      const result = await login({
        email: formState.inputs.email.value,
        password: formState.inputs.password.value,
        rememberMe
      }).unwrap();

      if (isTwoFactorChallengeResponse(result)) {
        navigate('/2fa/challenge', {
          state: {
            challengeToken: result.challengeToken,
            hasTotp: result.hasTotp,
            email: formState.inputs.email.value,
            urlBeforeRedirect
          }
        });
        return;
      }

      if (urlBeforeRedirect) navigate(`${urlBeforeRedirect}`);
      else navigate('/');
    } catch (err) {
      console.log('[Login Error]', err);
    }
  };

  const disabledCheck = () => {
    let style = {};

    const disabled = !formState.isValid;
    const mess = 'Log in';

    if (count === 3) {
      style = { display: 'none' };
    }
    return [disabled, mess, style];
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    if (!urlBeforeRedirect) navigate('/');
    // catch /login, if not after login in you get blank page because the urlBeforeRedirect is set and it makes the app navigate to /login after auth
    else if (urlBeforeRedirect === '/login') navigate('/');
    else navigate(`${urlBeforeRedirect}`);
  }, [isAuthenticated, navigate, urlBeforeRedirect]);

  useEffect(() => {
    if (isError) {
      setCount(count + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  return (
    <>
      <div className="auth-container">
        <ErrorModal
          error={isFetchBaseQueryError(loginError) ? loginError?.data?.message : ''}
          header="Login Error"
          onClear={() => {
            reset();
          }}
        />
        {isLoading && (
          <div className="center" style={{ clear: 'both' }}>
            <LoadingSpinner />
          </div>
        )}
        {!isLoading && (
          <Card className="authentication">
            <img src={Athenalogo} alt="logo" />

            <p className="headline">Welcome to Athena. Please log in</p>
            <form onSubmit={authSubmitHandler}>
              <Input
                element="input"
                id="email"
                type="email"
                placeholder="E-Mail"
                validators={[VALIDATOR_EMAIL()]}
                errorText="Please enter a valid email address."
                onInput={inputHandler}
              />
              <Input
                element="input"
                id="password"
                type="password"
                placeholder="Password"
                validators={[VALIDATOR_MINLENGTH(6)]}
                errorText="Incorect username or password"
                onInput={inputHandler}
              />
              <div className="button-submit" style={disabledCheck()[2]}>
                <Button type="submit" disabled={disabledCheck()[0]} onClick={buttonClickHandler}>
                  {disabledCheck()[1]}
                </Button>
              </div>
              <RememberMe checked={rememberMe} onChange={setRememberMe} />
            </form>
            <button onClick={onPasswordResetClick}>Password Reset</button>
          </Card>
        )}
        <SupportInfo />
      </div>
    </>
  );
};

export default Login;
